// Book of Business — presentational redesign of the (life-vertical) Clients
// roster. Dark-glass amber-key theme per src/hq/theme.js (the single
// palette source of truth, amber #F5A623), mirrors the interior treatment
// established in the Room primitive.
//
// IMPORTANT — this component is PRESENTATION ONLY. Every number rendered
// here is derived from data ClientsView already loaded and passed down as
// props (clients, riskByClient, producers, transactions-derived handlers).
// No new Supabase queries are added except one narrow, additive one: saving
// a quick note to the existing `clients.agent_notes` column (migration 100),
// which is the same column the workflow/message renderer already reads.
//
// Data-model note: this roster is LIFE-only (see ClientsView's matchesBook
// filter in CommissionTracker.jsx). The `clients` table has no plan_name,
// member_id, or plan-type column for life policies — those concepts live on
// the separate `health_policies` table used by the Health Portal, a
// different page. Rather than fabricate values for columns/charts that
// assume health-style data, this board:
//   - shows "LIFE" as the type badge (the one real, always-true vertical
//     value for everything in this view),
//   - shows "—" for Member ID (no such field exists on a life client row),
//   - relabels the "Clients by Plan Type" donut to "Clients by Draft
//     Frequency" (billing_mode), the one real categorical dimension this
//     data actually supports.
// See the redesign handoff for the full real-vs-honest-empty breakdown.

import React from 'react';
import {
  Users, FileText, DollarSign, AlertTriangle, Search, SlidersHorizontal,
  Columns3, Download, Plus, RefreshCw, MoreHorizontal, X, ChevronRight,
  ChevronLeft, Phone, Mail, MapPin, Calendar, Shield, StickyNote, Clock,
  Info, Eye, Lock, Mic, ClipboardList, FolderOpen, ListChecks,
  LayoutGrid, ExternalLink,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { readPersistedJwtDirect } from '../lib/authedFetch';

// ── gated PHI fetch helper ───────────────────────────────────────────
// Same pattern as src/uw/UnderwritingAgentView.jsx / src/medicare/
// MedicareClientDetailView.jsx's local apiFetch: bearer JWT read
// straight from localStorage (no getSession() round trip — see
// src/lib/authedFetch.js's header comment on why), JSON-parse
// defensively, throw on non-2xx so callers can catch() a single path.
async function apiFetch(path, opts = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: {
      'content-type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const ct = res.headers.get('content-type') || '';
  const body = ct.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const msg = (body && body.error) || (typeof body === 'string' ? body.slice(0, 200) : `HTTP ${res.status}`);
    throw new Error(msg);
  }
  return body;
}

// ── palette (keyed to src/hq/theme.js — amber is the interior color) ──
const palette = {
  card: 'rgba(20,23,30,0.82)',
  cardSoft: 'rgba(255,255,255,0.03)',
  border: 'rgba(245,166,35,0.20)',
  borderStrong: 'rgba(245,166,35,0.45)',
  text: '#eef1f6',
  textSecondary: '#b7c0cc',
  textFaint: '#7d8794',
  amber: '#F5A623',
  amberBright: '#FFC457',
  amberSoft: 'rgba(245,166,35,0.12)',
  emerald: '#34d399',
  emeraldSoft: 'rgba(52,211,153,0.12)',
  alert: '#f87171',
  alertSoft: 'rgba(248,113,113,0.12)',
  violet: '#a78bfa',
  trackBg: 'rgba(255,255,255,0.06)',
};
const fontSerif = "'Fraunces', Georgia, serif";
const fontMono = "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace";

const STATUS_META = {
  active:    { label: 'Active',    color: palette.emerald, soft: palette.emeraldSoft },
  nsf:       { label: 'NSF',       color: palette.amber,    soft: palette.amberSoft },
  lapsed:    { label: 'Lapsed',    color: palette.alert,   soft: palette.alertSoft },
  cancelled: { label: 'Cancelled', color: palette.textFaint, soft: 'rgba(255,255,255,0.06)' },
};
function statusMeta(status) {
  return STATUS_META[status] || STATUS_META.active;
}

// ── formatting helpers ──────────────────────────────────────────────
function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
function fmtMoney(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return 'Not tracked';
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtMoneyCompact(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return 'Not tracked';
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
function fmtDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}`.length <= 10 ? `${dateStr}T00:00:00` : dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
// Monthly-equivalent of draft_amount, normalized off the client's own
// billing_mode. Presentation-only unit conversion of a real field — not a
// new source of truth for billing (the lifecycle/draft crons own that).
function monthlyPremiumOf(c) {
  const amt = Number(c.draft_amount);
  if (!Number.isFinite(amt) || amt <= 0) return 0;
  const mode = c.billing_mode || 'monthly';
  if (mode === 'annual') return amt / 12;
  if (mode === 'quarterly') return amt / 3;
  return amt;
}
// Age, computed client-side and NEVER from a rendered raw DOB. Prefers
// birth_year (migration 180's "Option 2" de-identified column — the
// owner-approved design explicitly calls a lone birth year
// "non-sensitive in isolation"), falling back to the legacy plaintext
// `dob` column ONLY to compute a number, never to display the date
// itself. Full DOB only ever renders after an explicit, audited
// api/clients/dob-reveal.js call (see ClientDrawer below).
function ageOf(c) {
  const now = new Date();
  if (Number.isFinite(Number(c.birth_year)) && Number(c.birth_year) > 1900) {
    let age = now.getFullYear() - Number(c.birth_year);
    if (Number.isFinite(Number(c.birth_month)) && Number.isFinite(Number(c.birth_day))) {
      const hadBirthdayThisYear = (now.getMonth() + 1 > Number(c.birth_month)) ||
        (now.getMonth() + 1 === Number(c.birth_month) && now.getDate() >= Number(c.birth_day));
      if (!hadBirthdayThisYear) age -= 1;
    }
    return age > 0 && age < 120 ? age : null;
  }
  if (c.dob) {
    const d = new Date(`${c.dob}`.length <= 10 ? `${c.dob}T00:00:00` : c.dob);
    if (!Number.isNaN(d.getTime())) {
      let age = now.getFullYear() - d.getFullYear();
      const hadBirthday = now.getMonth() > d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() >= d.getDate());
      if (!hadBirthday) age -= 1;
      return age > 0 && age < 120 ? age : null;
    }
  }
  return null;
}

// $0.00 · subsidized — a $0 monthly premium traces to a real subsidy
// flag (sector === 'aca_private'), not a data-entry gap, so it never
// reads like an error. If we can't tell it's subsidized, plain $0.00.
function isMarketplacePlan(c) {
  return c.sector === 'aca_private';
}
function premiumDisplay(c) {
  const raw = c.draft_amount;
  const hasRaw = raw !== null && raw !== undefined && raw !== '' && Number.isFinite(Number(raw));
  if (!hasRaw) return { text: 'Not tracked', known: false };
  if (Number(raw) === 0) {
    return { text: isMarketplacePlan(c) ? `${fmtMoney(0)} · subsidized` : fmtMoney(0), known: true, zero: true };
  }
  return { text: `${fmtMoney(monthlyPremiumOf(c))}/mo`, known: true, zero: false };
}

// Real "next renewal" proxy: issue_date + 1 year. Returns a Date or null —
// null when the record has no issue_date (never fabricated).
function nextRenewalOf(c) {
  if (!c.issue_date) return null;
  const d = new Date(`${c.issue_date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const next = new Date(d.getTime());
  next.setFullYear(next.getFullYear() + 1);
  const now = new Date();
  let safety = 20;
  while (next < now && safety-- > 0) next.setFullYear(next.getFullYear() + 1);
  return next;
}

function downloadCsv(filename, headerRow, rows) {
  const all = [headerRow, ...rows];
  const csv = all.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── small presentational pieces ─────────────────────────────────────
function Avatar({ name, size = 32, tone = palette.amber }) {
  return (
    <div
      style={{
        flexShrink: 0, width: size, height: size, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `${tone}22`, border: `1px solid ${tone}55`,
        color: tone, fontFamily: fontMono, fontWeight: 700,
        fontSize: size * 0.36,
      }}
    >
      {initials(name)}
    </div>
  );
}

function StatusPill({ status }) {
  const meta = statusMeta(status || 'active');
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontFamily: fontMono, fontSize: 10, fontWeight: 700,
        letterSpacing: '0.06em', textTransform: 'uppercase',
        padding: '3px 8px', borderRadius: 999,
        color: meta.color, background: meta.soft, border: `1px solid ${meta.color}55`,
        whiteSpace: 'nowrap',
      }}
    >
      {meta.label}
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, sub, tone = palette.amber, tooltip, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        background: palette.card, backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
        border: `1px solid ${palette.border}`, borderRadius: 14, padding: '16px 18px',
        display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: fontMono, fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: palette.textSecondary, minWidth: 0 }}>
          {label}
          {tooltip && (
            <span
              tabIndex={0}
              role="img"
              aria-label={`How ${label} is calculated: ${tooltip}`}
              title={tooltip}
              style={{ display: 'inline-flex', color: palette.textFaint, cursor: 'help', flexShrink: 0 }}
            >
              <Info size={11} />
            </span>
          )}
        </span>
        <div style={{ width: 30, height: 30, borderRadius: 8, background: `${tone}1a`, border: `1px solid ${tone}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={15} color={tone} />
        </div>
      </div>
      <div style={{ fontFamily: fontMono, fontSize: 24, fontWeight: 700, color: palette.text, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: palette.textFaint }}>{sub}</div>}
    </div>
  );
}

// Self-contained inline-SVG donut. No chart library. data: [{label,value,color}]
function Donut({ data, size = 116, thickness = 15 }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  const r = (size - thickness) / 2;
  const c = size / 2;
  const circumference = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      {total === 0 ? (
        <circle cx={c} cy={c} r={r} fill="none" stroke={palette.trackBg} strokeWidth={thickness} />
      ) : (
        <g transform={`rotate(-90 ${c} ${c})`}>
          {data.filter(d => d.value > 0).map((d, i) => {
            const frac = d.value / total;
            const dash = frac * circumference;
            const gap = circumference - dash;
            const el = (
              <circle
                key={i} cx={c} cy={c} r={r} fill="none" stroke={d.color}
                strokeWidth={thickness} strokeDasharray={`${dash} ${gap}`}
                strokeDashoffset={-offset} strokeLinecap={data.filter(x=>x.value>0).length > 1 ? 'butt' : 'round'}
              />
            );
            offset += dash;
            return el;
          })}
        </g>
      )}
      <text x={c} y={c - 3} textAnchor="middle" fontFamily={fontMono} fontSize={18} fontWeight={700} fill={palette.text}>
        {total}
      </text>
      <text x={c} y={c + 13} textAnchor="middle" fontFamily={fontMono} fontSize={8.5} fill={palette.textFaint} letterSpacing="0.08em">
        TOTAL
      </text>
    </svg>
  );
}

function ChartCard({ title, caption, children, testId }) {
  return (
    <div data-testid={testId} style={{ background: palette.card, backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', border: `1px solid ${palette.border}`, borderRadius: 14, padding: 18, minWidth: 0 }}>
      <div style={{ fontFamily: fontSerif, fontSize: 14.5, fontWeight: 600, color: palette.text, marginBottom: 2 }}>{title}</div>
      {caption && <div style={{ fontSize: 11, color: palette.textFaint, marginBottom: 12 }}>{caption}</div>}
      {children}
    </div>
  );
}

function Legend({ data }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      {data.map((d, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, minWidth: 0 }}>
          <span style={{ width: 9, height: 9, borderRadius: 3, background: d.color, flexShrink: 0 }} />
          <span style={{ color: palette.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>{d.label}</span>
          <span style={{ fontFamily: fontMono, color: palette.text, fontWeight: 600, flexShrink: 0 }}>
            {d.value}{total > 0 ? ` (${Math.round((d.value / total) * 100)}%)` : ''}
          </span>
        </div>
      ))}
      {data.length === 0 && <div style={{ fontSize: 12, color: palette.textFaint, fontStyle: 'italic' }}>No data yet.</div>}
    </div>
  );
}

function BarRows({ data }) {
  const max = Math.max(1, ...data.map(d => d.value));
  if (data.length === 0) {
    return <div style={{ fontSize: 12, color: palette.textFaint, fontStyle: 'italic' }}>No carriers on the book yet.</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {data.map((d, i) => (
        <div key={i} style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, marginBottom: 3 }}>
            <span style={{ color: palette.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{d.label}</span>
            <span style={{ fontFamily: fontMono, color: palette.text, fontWeight: 600, flexShrink: 0, marginLeft: 8 }}>{d.value}</span>
          </div>
          <div style={{ height: 7, borderRadius: 999, background: palette.trackBg, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(d.value / max) * 100}%`, background: `linear-gradient(90deg, ${palette.amber}, ${palette.amberBright})`, borderRadius: 999 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── filter / column popovers ────────────────────────────────────────
function Popover({ open, onClose, anchorRight = false, children }) {
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
      <div
        style={{
          position: 'absolute', top: 'calc(100% + 6px)', [anchorRight ? 'right' : 'left']: 0,
          zIndex: 41, background: '#181b22', border: `1px solid ${palette.borderStrong}`,
          borderRadius: 12, padding: 14, minWidth: 240, boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </>
  );
}

function selStyle() {
  return {
    fontSize: 12.5, color: palette.text, background: 'rgba(255,255,255,0.04)',
    border: `1px solid ${palette.border}`, borderRadius: 8, padding: '7px 10px',
    outline: 'none', minWidth: 0,
  };
}
function btnStyle(active) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600,
    padding: '7px 12px', borderRadius: 8, cursor: 'pointer',
    border: `1px solid ${active ? palette.borderStrong : palette.border}`,
    background: active ? palette.amberSoft : 'rgba(255,255,255,0.03)',
    color: active ? palette.amberBright : palette.textSecondary,
  };
}

// Care Details PHI row — masked/count-only until the operator clicks
// "View", which calls the gated GET /api/health/client-medical-profile
// with a ?field= marker (see api/health/client-medical-profile.js —
// that marker is what triggers the audit_events write). `revealed`
// is the sentinel: null = not yet fetched (render masked), otherwise
// the real value (string, possibly '', or an array, possibly empty)
// straight from the audited response.
function PhiRow({ label, revealed, loading, onView, isList, emptyText }) {
  const isRevealed = revealed !== null && revealed !== undefined;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, padding: '9px 11px', background: palette.cardSoft, border: `1px solid ${palette.border}`, borderRadius: 8 }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 10.5, color: palette.textFaint, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
        {!isRevealed ? (
          <div style={{ fontFamily: fontMono, fontSize: 13, color: palette.textFaint, letterSpacing: '0.2em' }}>•••</div>
        ) : isList ? (
          revealed.length === 0 ? (
            <div style={{ fontSize: 12.5, color: palette.textFaint, fontStyle: 'italic' }}>{emptyText}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ fontSize: 10.5, color: palette.amber, fontWeight: 700 }}>{revealed.length} medication{revealed.length === 1 ? '' : 's'}</div>
              {revealed.map((rx, i) => (
                <div key={i} style={{ fontSize: 12.5, color: palette.text }}>{rx?.name || 'Unnamed'}{rx?.dosage ? ` · ${rx.dosage}` : ''}</div>
              ))}
            </div>
          )
        ) : revealed ? (
          <div style={{ fontSize: 12.5, color: palette.text, fontWeight: 600 }}>{revealed}</div>
        ) : (
          <div style={{ fontSize: 12.5, color: palette.textFaint, fontStyle: 'italic' }}>{emptyText}</div>
        )}
      </div>
      {!isRevealed && (
        <button
          type="button"
          onClick={onView}
          disabled={loading}
          title="Reveal — this fetch is audited (audit_events)."
          style={{ ...btnStyle(false), padding: '5px 10px', fontSize: 10.5, flexShrink: 0, opacity: loading ? 0.6 : 1 }}
        >
          {loading ? 'Loading…' : (<><Eye size={11} /> View</>)}
        </button>
      )}
    </div>
  );
}

// ── right-side client detail drawer ─────────────────────────────────
function ClientDrawer({ client, riskByClient, agentName, onClose, onEdit, onDelivery, showToast, refreshClients, estimateNextDraft, onGotoTab }) {
  const [activeTab, setActiveTab] = React.useState('overview');
  const [noteDraft, setNoteDraft] = React.useState('');
  const [savingNote, setSavingNote] = React.useState(false);
  const [voiceListening, setVoiceListening] = React.useState(false);
  const voiceRecRef = React.useRef(null);

  // DOB reveal — audited via POST /api/clients/dob-reveal. Transient
  // only: auto-clears 20s after reveal so a full plaintext DOB never
  // lingers in state/DOM longer than the operator needs it (mirrors
  // src/medicare/MedicareClientDetailView.jsx's MBI reveal pattern).
  const [dobRevealed, setDobRevealed] = React.useState(null);
  const [dobRevealing, setDobRevealing] = React.useState(false);
  const dobTimerRef = React.useRef(null);

  // Care Details (clinical PHI) reveal — each field independently
  // audited via GET /api/health/client-medical-profile?field=. null =
  // not yet fetched (render masked); '' / [] = fetched and honestly
  // empty; any other value = the real, revealed field.
  const [phiRevealed, setPhiRevealed] = React.useState({ pcp: null, pharmacy: null, prescriptions: null });
  const [phiLoadingField, setPhiLoadingField] = React.useState(null);

  React.useEffect(() => {
    setActiveTab('overview');
    setNoteDraft('');
    setDobRevealed(null);
    setPhiRevealed({ pcp: null, pharmacy: null, prescriptions: null });
    if (dobTimerRef.current) { clearTimeout(dobTimerRef.current); dobTimerRef.current = null; }
    if (voiceRecRef.current) { try { voiceRecRef.current.stop(); } catch { /* already stopped */ } }
    setVoiceListening(false);
  }, [client?.id]);
  React.useEffect(() => () => { if (dobTimerRef.current) clearTimeout(dobTimerRef.current); }, []);

  if (!client) return null;
  const c = client;
  const risk = riskByClient?.[c.id];
  const renewal = nextRenewalOf(c);
  const consent = Array.isArray(c.consent) ? c.consent[0] : c.consent;
  const nextDraft = typeof estimateNextDraft === 'function' ? estimateNextDraft(c) : null;
  const premium = premiumDisplay(c);
  const age = ageOf(c);
  const hasKnownDob = !!(c.birth_year || c.dob);

  async function revealDob() {
    if (dobRevealing || dobRevealed || !c.id) return;
    setDobRevealing(true);
    try {
      const r = await apiFetch('/api/clients/dob-reveal', {
        method: 'POST',
        body: JSON.stringify({ client_id: c.id, reason: 'book_of_business_drawer' }),
      });
      setDobRevealed(r.dob || null);
      if (dobTimerRef.current) clearTimeout(dobTimerRef.current);
      dobTimerRef.current = setTimeout(() => setDobRevealed(null), 20000);
    } catch (e) {
      showToast(e.message === 'dob_unavailable' || e.message === 'dob_decrypt_failed'
        ? "DOB isn't available to decrypt yet for this client."
        : (e.message || 'Reveal failed.'));
    }
    setDobRevealing(false);
  }

  async function revealPhiField(field) {
    if (phiLoadingField || phiRevealed[field] !== null || !c.id) return;
    setPhiLoadingField(field);
    try {
      const r = await apiFetch(`/api/health/client-medical-profile?client_id=${encodeURIComponent(c.id)}&field=${encodeURIComponent(field)}`);
      const profile = r?.profile || null;
      let value;
      if (field === 'pcp') value = profile?.primary_doctor || '';
      else if (field === 'pharmacy') value = profile?.preferred_pharmacy || '';
      else value = Array.isArray(profile?.current_prescriptions) ? profile.current_prescriptions : [];
      setPhiRevealed(prev => ({ ...prev, [field]: value }));
    } catch (e) {
      showToast(e.message || "Couldn't load care details.");
    }
    setPhiLoadingField(null);
  }

  async function saveNote() {
    const text = noteDraft.trim();
    if (!text || !c.id) return;
    setSavingNote(true);
    try {
      const stamp = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const merged = [c.agent_notes, `[${stamp}] ${text}`].filter(Boolean).join('\n');
      const { error } = await supabase.from('clients').update({ agent_notes: merged }).eq('id', c.id);
      if (error) { showToast(`Note failed to save: ${error.message}`); return; }
      showToast('Note added.');
      setNoteDraft('');
      if (refreshClients) await refreshClients();
    } finally {
      setSavingNote(false);
    }
  }

  // Voice-note dictation — same Web Speech pattern as
  // src/leads/FastAddDrawer.jsx's toggleVoice, wired to the note
  // draft field instead of the lead-notes field.
  function toggleVoice() {
    if (voiceListening) {
      try { voiceRecRef.current?.stop(); } catch { /* already stopped */ }
      setVoiceListening(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { showToast("Voice input isn't supported in this browser. Type the note instead."); return; }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';
    let base = noteDraft;
    rec.onresult = (e) => {
      let interim = '';
      let finalPart = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalPart += t + ' ';
        else interim += t;
      }
      if (finalPart) { base = (base + ' ' + finalPart).trim(); setNoteDraft(base); }
      else { setNoteDraft((base + ' ' + interim).trim()); }
    };
    rec.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        showToast('Mic is blocked — allow it in your browser to use voice notes.');
      }
      setVoiceListening(false);
    };
    rec.onend = () => setVoiceListening(false);
    voiceRecRef.current = rec;
    setVoiceListening(true);
    try { rec.start(); } catch { /* already started */ }
  }

  const Row = ({ label, value }) => value ? (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12.5, padding: '6px 0', borderBottom: `1px solid ${palette.border}` }}>
      <span style={{ color: palette.textFaint }}>{label}</span>
      <span style={{ color: palette.text, fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </div>
  ) : null;

  const notesCount = (c.agent_notes || '').split('\n').map(s => s.trim()).filter(Boolean).length;
  const policiesCount = (c.policy || c.carrier || premium.known) ? 1 : 0;
  const TABS = [
    { key: 'overview', label: 'Overview', icon: LayoutGrid, badge: null },
    { key: 'policies', label: 'Policies', icon: Shield, badge: policiesCount },
    { key: 'notes', label: 'Notes', icon: StickyNote, badge: notesCount },
    { key: 'files', label: 'Files', icon: FolderOpen, badge: 0 },
    { key: 'tasks', label: 'Tasks', icon: ListChecks, badge: 0 },
  ];

  const sectionLabelStyle = { fontFamily: fontMono, fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: palette.amber, marginBottom: 8 };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 60 }} />
      <div
        data-testid="client-panel"
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(440px, 100vw)', zIndex: 61,
          background: '#14161c', borderLeft: `1px solid ${palette.borderStrong}`,
          overflowY: 'auto', color: palette.text, boxShadow: '-16px 0 40px rgba(0,0,0,0.4)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Hero */}
        <div style={{ padding: '18px 20px 0', borderBottom: `1px solid ${palette.border}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
              <Avatar name={c.name} size={44} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: fontSerif, fontSize: 17, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name || 'Unnamed client'}</div>
                <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <StatusPill status={c.billing_status || 'active'} />
                </div>
              </div>
            </div>
            <button onClick={onClose} aria-label="Close" style={{ background: 'transparent', border: 'none', color: palette.textFaint, cursor: 'pointer', flexShrink: 0 }}>
              <X size={18} />
            </button>
          </div>

          {/* Age + masked DOB — full DOB only ever shows after the
              audited reveal below; before that, age alone (from the
              de-identified birth_year column where available). */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0 12px', fontSize: 12.5, color: palette.textSecondary }}>
            <Calendar size={13} color={palette.textFaint} />
            {age != null ? (
              <span>{dobRevealed ? `${fmtDate(dobRevealed) || dobRevealed} (${age})` : `Age ${age}`}</span>
            ) : dobRevealed ? (
              <span>{fmtDate(dobRevealed) || dobRevealed}</span>
            ) : (
              <span style={{ color: palette.textFaint, fontStyle: 'italic' }}>Age not on file</span>
            )}
            {!dobRevealed && hasKnownDob && (
              <button
                type="button"
                onClick={revealDob}
                disabled={dobRevealing}
                title="Reveal the full date of birth (audited)"
                style={{ background: 'transparent', border: 'none', color: palette.amber, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 700, padding: 0 }}
              >
                {dobRevealing ? 'Revealing…' : (<><Eye size={11} /> View DOB</>)}
              </button>
            )}
          </div>

          {/* Icon tabs w/ real count badges */}
          <div style={{ display: 'flex', gap: 2 }}>
            {TABS.map(t => (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                title={t.label}
                style={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                  padding: '6px 4px 9px', background: 'transparent', border: 'none', cursor: 'pointer',
                  borderBottom: activeTab === t.key ? `2px solid ${palette.amber}` : '2px solid transparent',
                  color: activeTab === t.key ? palette.amberBright : palette.textFaint,
                }}
              >
                <span style={{ position: 'relative', display: 'inline-flex' }}>
                  <t.icon size={15} />
                  {t.badge > 0 && (
                    <span style={{ position: 'absolute', top: -6, right: -9, background: palette.amber, color: '#1a1200', fontSize: 8, fontWeight: 800, borderRadius: 999, minWidth: 13, height: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 2px', lineHeight: 1 }}>
                      {t.badge > 9 ? '9+' : t.badge}
                    </span>
                  )}
                </span>
                <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '0.02em' }}>{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 18, flex: 1 }}>
          {activeTab === 'overview' && (
            <>
              {/* Contact */}
              <section>
                <div style={sectionLabelStyle}>Contact</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {c.phone && <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}><Phone size={13} color={palette.textFaint} /> {c.phone}</div>}
                  {c.email && <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}><Mail size={13} color={palette.textFaint} /> {c.email}</div>}
                  {c.address && <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}><MapPin size={13} color={palette.textFaint} /> {[c.address, c.state_abbr].filter(Boolean).join(', ')}</div>}
                  {!c.phone && !c.email && !c.address && <div style={{ fontSize: 12, color: palette.textFaint, fontStyle: 'italic' }}>No contact details on file.</div>}
                </div>
              </section>

              {/* Consent — care vs marketing are separate opt-ins (see consent
                  table, migration 019). Surfaced here rather than the table so
                  the columns match the approved layout, but the real values are
                  still one click away. */}
              <section>
                <div style={sectionLabelStyle}>Consent</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: consent?.opted_out ? palette.alert : consent?.care_opted_in ? palette.emerald : palette.textFaint }}>
                    {consent?.opted_out ? 'OPTED OUT' : consent?.care_opted_in ? '✓ Care opted in' : 'Care off'}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: consent?.marketing_ok ? palette.emerald : palette.textFaint }}>
                    {consent?.marketing_ok ? '✓ Marketing opted in' : 'No marketing'}
                  </span>
                </div>
              </section>

              {/* Engagement — last contact / next follow up / lead source
                  have no activity-feed or lead-attribution table loaded
                  into this view, so all three are honestly shown as
                  unavailable rather than fabricated. */}
              <section>
                <div style={sectionLabelStyle}>Engagement</div>
                <Row label="Last contact" value="Not tracked" />
                <Row label="Next follow up" value="Not tracked" />
                <Row label="Lead source" value="Not tracked" />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <Avatar name={agentName} size={28} tone={palette.violet} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 10, color: palette.textFaint }}>Assigned agent</div>
                      <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{agentName}</div>
                    </div>
                  </div>
                  <button onClick={() => onEdit(c)} style={{ ...btnStyle(false), padding: '5px 10px', fontSize: 11 }}>Change</button>
                </div>
              </section>

              {/* Tags — no tags column exists on public.clients (checked
                  migration 019 + later ALTERs); rendered honestly empty
                  rather than backing an "Add Tag" control with nowhere
                  real to write. */}
              <section>
                <div style={sectionLabelStyle}>Tags</div>
                <div style={{ fontSize: 12, color: palette.textFaint, fontStyle: 'italic' }}>
                  No tags yet — tagging isn't wired to a client field in this book yet.
                </div>
              </section>
            </>
          )}

          {activeTab === 'policies' && (
            <>
              {/* Active policy */}
              <section data-testid="active-policy">
                <div style={sectionLabelStyle}>Active Policy</div>
                <div style={{ background: palette.cardSoft, border: `1px solid ${palette.border}`, borderRadius: 10, padding: '10px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <Shield size={14} color={palette.amber} />
                    <span style={{ fontWeight: 700, fontSize: 13.5 }}>{c.carrier || 'Carrier not set'}</span>
                  </div>
                  <Row label="Policy #" value={c.policy ? <span style={{ fontFamily: fontMono }}>{c.policy}</span> : null} />
                  <Row label="Member ID" value={null /* not a field on life clients — honestly omitted */} />
                  <Row label="Effective date" value={fmtDate(c.issue_date)} />
                  <Row label="Premium" value={premium.known ? premium.text : null} />
                  <Row label="Next renewal" value={renewal ? renewal.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null} />
                  <Row label="Next draft (est.)" value={nextDraft ? nextDraft.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null} />
                  {c.lifecycle_paused && (
                    <div style={{ marginTop: 6, fontSize: 11, color: palette.amber }}>Lifecycle sends paused for this client.</div>
                  )}
                  {risk && risk.score >= 30 && (
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: risk.level === 'high' ? palette.alert : palette.amber }}>
                      <AlertTriangle size={13} /> {risk.level === 'high' ? 'High' : 'Medium'} payment risk, {risk.score}/100 ({risk.count90} NSF in 90d)
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => onDelivery(c)}
                    title="Opens the policy delivery / application summary for this client."
                    style={{ ...btnStyle(false), marginTop: 10, padding: '6px 11px', fontSize: 11, width: '100%', justifyContent: 'center' }}
                  >
                    <ExternalLink size={12} /> View Application
                  </button>
                </div>
              </section>

              {/* Care Details — PHI Vault. Reveal-on-demand only; every
                  "View" click below hits the audited endpoint. */}
              <section data-testid="phi-section">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                  <span style={sectionLabelStyle}>Care Details</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: fontMono, fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '2px 7px', borderRadius: 999, background: palette.amberSoft, color: palette.amber, border: `1px solid ${palette.borderStrong}` }}>
                    <Lock size={9} /> PHI Vault · reads logged
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <PhiRow
                    label="Primary Care Physician"
                    revealed={phiRevealed.pcp}
                    loading={phiLoadingField === 'pcp'}
                    onView={() => revealPhiField('pcp')}
                    emptyText="No PCP on file."
                  />
                  <PhiRow
                    label="Pharmacy"
                    revealed={phiRevealed.pharmacy}
                    loading={phiLoadingField === 'pharmacy'}
                    onView={() => revealPhiField('pharmacy')}
                    emptyText="No pharmacy on file."
                  />
                  <PhiRow
                    label="Prescription Drugs"
                    revealed={phiRevealed.prescriptions}
                    loading={phiLoadingField === 'prescriptions'}
                    onView={() => revealPhiField('prescriptions')}
                    isList
                    emptyText="No prescriptions on file."
                  />
                </div>
              </section>
            </>
          )}

          {activeTab === 'notes' && (
            <section>
              <div style={sectionLabelStyle}>Notes</div>
              {c.agent_notes && (
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 12.5, color: palette.textSecondary, background: palette.cardSoft, border: `1px solid ${palette.border}`, borderRadius: 10, padding: '10px 12px', marginBottom: 8, maxHeight: 220, overflowY: 'auto' }}>
                  {c.agent_notes}
                </div>
              )}
              {!c.agent_notes && (
                <div style={{ fontSize: 12, color: palette.textFaint, fontStyle: 'italic', marginBottom: 8 }}>No notes yet.</div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  value={noteDraft}
                  onChange={e => setNoteDraft(e.target.value)}
                  placeholder="Add a note…"
                  style={{ ...selStyle(), flex: 1 }}
                  onKeyDown={e => { if (e.key === 'Enter') saveNote(); }}
                />
                <button
                  type="button"
                  onClick={toggleVoice}
                  title={voiceListening ? 'Stop dictation' : 'Dictate this note'}
                  style={{ ...btnStyle(voiceListening), padding: '7px 9px' }}
                >
                  <Mic size={13} color={voiceListening ? palette.alert : undefined} />
                </button>
                <button onClick={saveNote} disabled={savingNote || !noteDraft.trim()} style={{ ...btnStyle(true), opacity: (savingNote || !noteDraft.trim()) ? 0.5 : 1 }}>
                  <StickyNote size={13} /> Add
                </button>
              </div>
            </section>
          )}

          {activeTab === 'files' && (
            <section>
              <div style={sectionLabelStyle}>Files</div>
              <div style={{ fontSize: 12, color: palette.textFaint, fontStyle: 'italic', marginBottom: 10 }}>
                No files uploaded. Book of Business isn't wired to a document store yet.
              </div>
              <button onClick={() => onDelivery(c)} style={{ ...btnStyle(false) }}>
                <FileText size={13} /> Open Policy Delivery Summary
              </button>
            </section>
          )}

          {activeTab === 'tasks' && (
            <section>
              <div style={sectionLabelStyle}>Tasks</div>
              <div style={{ fontSize: 12, color: palette.textFaint, fontStyle: 'italic', marginBottom: 10 }}>
                Follow-ups aren't tracked on the client record in Book of Business yet.
              </div>
              {typeof onGotoTab === 'function' && (
                <button onClick={() => onGotoTab('follow_up')} style={{ ...btnStyle(false) }}>
                  <ClipboardList size={13} /> Open Follow-Up Engine
                </button>
              )}
            </section>
          )}
        </div>

        <div style={{ padding: '4px 20px 20px', display: 'flex', gap: 8, flexShrink: 0 }}>
          <button onClick={() => onEdit(c)} style={{ ...btnStyle(true), flex: 1, justifyContent: 'center' }}>Edit client</button>
          <button onClick={() => onDelivery(c)} style={{ ...btnStyle(false), flex: 1, justifyContent: 'center' }}><FileText size={13} /> Delivery summary</button>
        </div>
      </div>
    </>
  );
}

// ── main board ───────────────────────────────────────────────────────
export default function BookOfBusinessBoard({
  clients = [],
  riskByClient = {},
  getAgentName = () => 'Unassigned',
  search = '',
  setSearch = () => {},
  onAddClient = () => {},
  onEditClient = () => {},
  onDeliverySummary = () => {},
  onSyncBook = () => {},
  syncing = false,
  myProdNum = '',
  downlinePolicyCount = 0,
  isDownlinePolicy = () => false,
  showToast = () => {},
  refreshClients = () => {},
  estimateNextDraft = () => null,
  isDemo = false,
  onGotoTab,
}) {
  const [statusTab, setStatusTab] = React.useState('all');
  const [carrierFilter, setCarrierFilter] = React.useState('all');
  const [statusDropdown, setStatusDropdown] = React.useState('all');
  const [agentFilter, setAgentFilter] = React.useState('all');
  const [riskOnly, setRiskOnly] = React.useState(false);
  const [downlineOnly, setDownlineOnly] = React.useState(false);
  const [missingPhoneOnly, setMissingPhoneOnly] = React.useState(false);
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [columnsOpen, setColumnsOpen] = React.useState(false);
  const [hiddenCols, setHiddenCols] = React.useState(() => new Set());
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [page, setPage] = React.useState(1);
  const [openMenuId, setOpenMenuId] = React.useState(null);
  const [selectedClient, setSelectedClient] = React.useState(null);

  const KNOWN_STATUSES = React.useMemo(() => new Set(['active', 'nsf', 'lapsed', 'cancelled']), []);

  // ── KPIs — computed off the FULL life book, not the current filter/search ──
  const kpis = React.useMemo(() => {
    const total = clients.length;
    const activeClients = clients.filter(c => (c.billing_status || 'active') === 'active');
    const totalMonthly = activeClients.reduce((s, c) => s + monthlyPremiumOf(c), 0);
    const atRisk = new Set();
    for (const c of clients) {
      if (c.billing_status === 'nsf' || c.billing_status === 'lapsed') atRisk.add(c.id);
      const r = riskByClient[c.id];
      if (r && r.score >= 30) atRisk.add(c.id);
    }
    return {
      total,
      activePolicies: activeClients.length,
      totalMonthly,
      annualized: totalMonthly * 12,
      atRisk: atRisk.size,
    };
  }, [clients, riskByClient]);

  const statusCounts = React.useMemo(() => ({
    all:       clients.length,
    active:    clients.filter(c => (c.billing_status || 'active') === 'active').length,
    pending:   clients.filter(c => !KNOWN_STATUSES.has(c.billing_status || 'active')).length,
    lapsed:    clients.filter(c => c.billing_status === 'lapsed').length,
    cancelled: clients.filter(c => c.billing_status === 'cancelled').length,
  }), [clients, KNOWN_STATUSES]);

  const carrierOptions = React.useMemo(() => {
    const s = new Set(clients.map(c => (c.carrier || '').trim()).filter(Boolean));
    return Array.from(s).sort();
  }, [clients]);
  const statusOptions = React.useMemo(() => {
    const s = new Set(clients.map(c => c.billing_status || 'active'));
    return Array.from(s);
  }, [clients]);
  const agentOptions = React.useMemo(() => {
    const s = new Set(clients.map(c => (c.prod_num || '').trim()).filter(Boolean));
    return Array.from(s).map(pn => ({ pn, name: getAgentName(pn) })).sort((a, b) => a.name.localeCompare(b.name));
  }, [clients, getAgentName]);

  const filteredRows = React.useMemo(() => {
    return clients.filter(c => {
      if (statusTab === 'active' && (c.billing_status || 'active') !== 'active') return false;
      if (statusTab === 'lapsed' && c.billing_status !== 'lapsed') return false;
      if (statusTab === 'cancelled' && c.billing_status !== 'cancelled') return false;
      if (statusTab === 'pending' && KNOWN_STATUSES.has(c.billing_status || 'active')) return false;
      if (carrierFilter !== 'all' && (c.carrier || '').trim() !== carrierFilter) return false;
      if (statusDropdown !== 'all' && (c.billing_status || 'active') !== statusDropdown) return false;
      if (agentFilter !== 'all' && (c.prod_num || '').trim() !== agentFilter) return false;
      if (riskOnly && !((riskByClient[c.id]?.score || 0) >= 30)) return false;
      if (downlineOnly && !isDownlinePolicy(c)) return false;
      if (missingPhoneOnly && c.phone) return false;
      if (search.trim()) {
        const s = search.toLowerCase();
        const hit = (c.name || '').toLowerCase().includes(s)
          || (c.policy || '').toLowerCase().includes(s)
          || (c.phone || '').includes(s)
          || (c.carrier || '').toLowerCase().includes(s)
          || (c.email || '').toLowerCase().includes(s);
        if (!hit) return false;
      }
      return true;
    });
  }, [clients, statusTab, carrierFilter, statusDropdown, agentFilter, riskOnly, downlineOnly, missingPhoneOnly, search, riskByClient, isDownlinePolicy, KNOWN_STATUSES]);

  React.useEffect(() => { setPage(1); }, [statusTab, carrierFilter, statusDropdown, agentFilter, riskOnly, downlineOnly, missingPhoneOnly, search, rowsPerPage]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const pageRows = filteredRows.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const activeFilterCount = [riskOnly, downlineOnly, missingPhoneOnly].filter(Boolean).length;

  function toggleCol(key) {
    setHiddenCols(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }
  const colVisible = (key) => !hiddenCols.has(key);

  function exportCsv() {
    const header = ['Name', 'Phone', 'Email', 'Policy #', 'Carrier', 'Status', 'Effective Date', 'Premium (Mo.)', 'Agent', 'Downline'];
    const rows = filteredRows.map(c => [
      c.name || '', c.phone || '', c.email || '', c.policy || '', c.carrier || '',
      c.billing_status || 'active', c.issue_date || '', monthlyPremiumOf(c).toFixed(2),
      getAgentName(c.prod_num), isDownlinePolicy(c) ? 'yes' : 'no',
    ]);
    downloadCsv(`book-of-business-${new Date().toISOString().slice(0, 10)}.csv`, header, rows);
    showToast(`Exported ${rows.length} client${rows.length === 1 ? '' : 's'}.`);
  }

  // ── chart data (real, book-wide) ──────────────────────────────────
  const draftFreqData = React.useMemo(() => {
    const counts = { monthly: 0, quarterly: 0, annual: 0 };
    for (const c of clients) counts[c.billing_mode || 'monthly'] = (counts[c.billing_mode || 'monthly'] || 0) + 1;
    return [
      { label: 'Monthly', value: counts.monthly || 0, color: palette.amber },
      { label: 'Quarterly', value: counts.quarterly || 0, color: palette.violet },
      { label: 'Annual', value: counts.annual || 0, color: palette.emerald },
    ];
  }, [clients]);

  const carrierChartData = React.useMemo(() => {
    const counts = new Map();
    for (const c of clients) {
      const label = (c.carrier || '').trim();
      if (!label) continue;
      counts.set(label, (counts.get(label) || 0) + 1);
    }
    return Array.from(counts.entries()).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value).slice(0, 6);
  }, [clients]);

  const statusChartData = React.useMemo(() => ([
    { label: 'Active', value: statusCounts.active, color: palette.emerald },
    { label: 'Pending', value: statusCounts.pending, color: palette.violet },
    { label: 'Lapsed', value: statusCounts.lapsed, color: palette.alert },
    { label: 'Cancelled', value: statusCounts.cancelled, color: palette.textFaint },
  ]), [statusCounts]);

  const upcomingRenewals = React.useMemo(() => {
    return clients
      .map(c => ({ c, renewal: nextRenewalOf(c) }))
      .filter(x => x.renewal)
      .sort((a, b) => a.renewal - b.renewal)
      .slice(0, 8);
  }, [clients]);

  const STATUS_TABS = [
    ['all', 'All Clients'],
    ['active', 'Active'],
    ['pending', 'Pending'],
    ['lapsed', 'Lapsed'],
    ['cancelled', 'Cancelled'],
  ];

  return (
    <div style={{ color: palette.text, fontFamily: "system-ui, -apple-system, sans-serif" }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 style={{ fontFamily: fontSerif, fontSize: 26, fontWeight: 600, margin: 0, color: palette.text }}>Book of Business</h1>
            {isDemo && (
              <span
                data-testid="sample-badge"
                style={{
                  fontFamily: fontMono, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                  textTransform: 'uppercase', padding: '3px 9px', borderRadius: 999,
                  color: palette.amber, background: palette.amberSoft, border: `1px solid ${palette.borderStrong}`,
                }}
              >Sample data</span>
            )}
          </div>
          <p style={{ fontSize: 13, color: palette.textSecondary, margin: '6px 0 0 0', maxWidth: 560 }}>
            Manage your clients, policies, and coverage all in one place.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={onSyncBook}
            disabled={syncing || !myProdNum}
            title={myProdNum
              ? `Pull every active policy you wrote (prod # ${myProdNum}) into the roster. Downline policies are excluded.`
              : 'Set your producer # in Settings first.'}
            style={{ ...btnStyle(false), opacity: (syncing || !myProdNum) ? 0.5 : 1 }}
          >
            <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Syncing…' : 'Sync Book'}
          </button>
          <button type="button" onClick={exportCsv} style={btnStyle(false)}>
            <Download size={13} /> Export
          </button>
          <button
            type="button"
            data-testid="btn-add-client"
            onClick={() => onAddClient()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, padding: '8px 14px', borderRadius: 8, border: 'none', background: palette.amber, color: '#1a1200', cursor: 'pointer' }}
          >
            <Plus size={14} /> Add Client
          </button>
        </div>
      </div>

      {downlinePolicyCount > 0 && (
        <div style={{ background: palette.amberSoft, border: `1px solid ${palette.border}`, borderRadius: 10, padding: '10px 14px', fontSize: 12, color: palette.textSecondary, marginBottom: 16, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <AlertTriangle size={14} color={palette.amber} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            <strong style={{ color: palette.amberBright }}>{downlinePolicyCount} downline polic{downlinePolicyCount === 1 ? 'y is' : 'ies are'}</strong> excluded from your roster and the lifecycle agent. Each downline producer's clients are texted from their own seat, not yours.
          </span>
        </div>
      )}

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        <KpiCard
          testId="kpi-clients"
          icon={Users} label="Total Clients" value={kpis.total.toLocaleString()} tone={palette.amber}
          tooltip="count(clients) for your life book — every row in this roster, any status."
        />
        <KpiCard
          icon={Shield} label="Active Policies" value={kpis.activePolicies.toLocaleString()} tone={palette.emerald}
          tooltip="count(clients) where billing_status = 'active'."
        />
        <KpiCard
          icon={DollarSign} label="Total Premium (Mo.)" value={fmtMoneyCompact(kpis.totalMonthly)} tone={palette.amber}
          tooltip="sum(monthly-equivalent draft_amount) across active clients only. Quarterly/annual drafts are normalized to a monthly figure (÷3 or ÷12) for comparison."
        />
        <KpiCard
          icon={DollarSign} label="Premium (Annualized)" value={fmtMoneyCompact(kpis.annualized)} tone={palette.violet}
          tooltip="Total Premium (Mo.) × 12. A deterministic projection, not a separate query."
        />
        <KpiCard
          testId="kpi-risk"
          icon={AlertTriangle} label="Policies at Risk" value={kpis.atRisk.toLocaleString()} tone={palette.alert}
          tooltip="Deterministic set: billing_status = 'nsf' OR 'lapsed', OR a payment-risk score ≥ 30/100 from nsf_events in the last 90 days (same model as the scan-client-payments cron, api/_paymentRisk.js). Each client counted once even if it hits more than one condition."
        />
      </div>

      {/* Status tab bar + Filters/Columns/Export */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {STATUS_TABS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setStatusTab(key)}
              style={{
                fontSize: 12, fontWeight: 700, padding: '7px 12px', borderRadius: 999, cursor: 'pointer',
                border: `1px solid ${statusTab === key ? palette.borderStrong : palette.border}`,
                background: statusTab === key ? palette.amber : 'rgba(255,255,255,0.03)',
                color: statusTab === key ? '#1a1200' : palette.textSecondary,
                whiteSpace: 'nowrap',
              }}
            >
              {label} · {statusCounts[key]}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', position: 'relative' }}>
          <div style={{ position: 'relative' }}>
            <button onClick={() => { setFiltersOpen(v => !v); setColumnsOpen(false); }} style={btnStyle(activeFilterCount > 0)}>
              <SlidersHorizontal size={13} /> Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </button>
            <Popover open={filtersOpen} onClose={() => setFiltersOpen(false)} anchorRight>
              <FilterPopoverBody
                riskOnly={riskOnly} setRiskOnly={setRiskOnly}
                downlineOnly={downlineOnly} setDownlineOnly={setDownlineOnly}
                missingPhoneOnly={missingPhoneOnly} setMissingPhoneOnly={setMissingPhoneOnly}
              />
            </Popover>
          </div>
          <div style={{ position: 'relative' }}>
            <button onClick={() => { setColumnsOpen(v => !v); setFiltersOpen(false); }} style={btnStyle(hiddenCols.size > 0)}>
              <Columns3 size={13} /> Columns
            </button>
            <Popover open={columnsOpen} onClose={() => setColumnsOpen(false)} anchorRight>
              <ColumnsPopoverBody hiddenCols={hiddenCols} toggleCol={toggleCol} />
            </Popover>
          </div>
        </div>
      </div>

      {/* Secondary filter row */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 200 }}>
          <Search size={14} color={palette.textFaint} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search name, policy, phone, email, carrier…"
            style={{ ...selStyle(), width: '100%', paddingLeft: 30 }}
          />
        </div>
        <select value={carrierFilter} onChange={e => setCarrierFilter(e.target.value)} style={selStyle()}>
          <option value="all">All Carriers</option>
          {carrierOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {/* Vestigial life-only product dropdown removed — product line is
            now chosen by the Book segmented control (Life / ACA·Private /
            Medicare / All) above the board in ClientsView. */}
        <select value={statusDropdown} onChange={e => setStatusDropdown(e.target.value)} style={selStyle()}>
          <option value="all">All Statuses</option>
          {statusOptions.map(s => <option key={s} value={s}>{statusMeta(s).label}</option>)}
        </select>
        <select value={agentFilter} onChange={e => setAgentFilter(e.target.value)} style={selStyle()}>
          <option value="all">All Agents</option>
          {agentOptions.map(a => <option key={a.pn} value={a.pn}>{a.name}</option>)}
        </select>
        <div style={{ position: 'relative' }}>
          <button onClick={() => { setFiltersOpen(v => !v); setColumnsOpen(false); }} style={btnStyle(activeFilterCount > 0)}>
            More Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </button>
        </div>
      </div>

      {/* Table */}
      {clients.length === 0 ? (
        <div style={{ background: palette.card, border: `1px solid ${palette.border}`, borderRadius: 14, padding: '48px 24px', textAlign: 'center' }}>
          <Users size={28} color={palette.amber} style={{ marginBottom: 10 }} />
          <div style={{ fontFamily: fontSerif, fontSize: 17, fontWeight: 600 }}>You haven't added any clients yet.</div>
          <div style={{ fontSize: 13, color: palette.textSecondary, marginTop: 6, maxWidth: 440, marginLeft: 'auto', marginRight: 'auto' }}>
            Add your first client at close so the lifecycle agent can start texting them, watching for renewals, and flagging retention risk.
          </div>
          <button onClick={() => onAddClient()} style={{ marginTop: 14, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, padding: '9px 16px', borderRadius: 8, border: 'none', background: palette.amber, color: '#1a1200', cursor: 'pointer' }}>
            <Plus size={14} /> Add your first client
          </button>
        </div>
      ) : (
        <div style={{ background: palette.card, backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', border: `1px solid ${palette.border}`, borderRadius: 14, overflow: 'hidden', marginBottom: 24 }}>
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="bob-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <Th>Client</Th>
                  <Th>Plan / Policy</Th>
                  <Th>Carrier</Th>
                  {colVisible('member_id') && <Th>Member ID</Th>}
                  <Th>Status</Th>
                  {colVisible('effective_date') && <Th>Effective Date</Th>}
                  <Th align="right">Premium (Mo.)</Th>
                  {colVisible('agent') && <Th>Agent</Th>}
                  <Th align="right"> </Th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr><td colSpan={9} style={{ padding: '28px 14px', textAlign: 'center', color: palette.textFaint, fontSize: 12.5 }}>No matches for these filters.</td></tr>
                ) : pageRows.map(c => {
                  const agentName = getAgentName(c.prod_num);
                  const downline = isDownlinePolicy(c);
                  return (
                    <tr
                      key={c.id}
                      onClick={() => setSelectedClient(c)}
                      style={{ borderTop: `1px solid ${palette.border}`, cursor: 'pointer' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <Td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                          <Avatar name={c.name} size={30} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700, color: palette.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>{c.name || 'Unnamed'}</div>
                            <div style={{ fontSize: 11, color: palette.textFaint }}>{c.phone || 'No phone on file'}</div>
                            {c.email && <div style={{ fontSize: 11, color: palette.textFaint, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>{c.email}</div>}
                          </div>
                        </div>
                      </Td>
                      <Td>
                        <span style={{ fontFamily: fontMono, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em', padding: '2px 6px', borderRadius: 5, background: palette.amberSoft, color: palette.amberBright, marginRight: 6 }}>{c.vertical === 'health' ? (c.sector === 'medicare' ? 'MEDICARE' : 'ACA') : 'LIFE'}</span>
                        <span style={{ color: palette.textSecondary }}>{c.policy || 'Not tracked'}</span>
                      </Td>
                      <Td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Avatar name={c.carrier} size={22} tone={palette.violet} />
                          <span>{c.carrier || 'Not tracked'}</span>
                        </div>
                      </Td>
                      {colVisible('member_id') && <Td><span style={{ fontFamily: fontMono, color: palette.textFaint }}>Not tracked</span></Td>}
                      <Td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                          <StatusPill status={c.billing_status || 'active'} />
                          {downline && (
                            <span title={`Written by prod # ${c.prod_num}`} style={{ fontFamily: fontMono, fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 5, background: 'rgba(255,255,255,0.06)', color: palette.textFaint }}>DOWNLINE</span>
                          )}
                          {c.lifecycle_paused && (
                            <span title="Lifecycle sends paused for this client" style={{ fontFamily: fontMono, fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 5, background: palette.amberSoft, color: palette.amber }}>PAUSED</span>
                          )}
                        </div>
                      </Td>
                      {colVisible('effective_date') && <Td>{fmtDate(c.issue_date) || <span style={{ color: palette.textFaint }}>Not tracked</span>}</Td>}
                      <Td align="right">
                        {(() => {
                          const p = premiumDisplay(c);
                          return (
                            <span style={{ fontFamily: fontMono, fontWeight: p.known ? 700 : 400, color: p.known ? palette.text : palette.textFaint }}>
                              {p.text}
                            </span>
                          );
                        })()}
                      </Td>
                      {colVisible('agent') && (
                        <Td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Avatar name={agentName} size={22} tone={palette.violet} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 110 }}>{agentName}</span>
                          </div>
                        </Td>
                      )}
                      <Td align="right">
                        <div style={{ position: 'relative', display: 'inline-block' }} onClick={e => e.stopPropagation()}>
                          <button onClick={() => setOpenMenuId(openMenuId === c.id ? null : c.id)} style={{ background: 'transparent', border: 'none', color: palette.textFaint, cursor: 'pointer', padding: 4 }}>
                            <MoreHorizontal size={16} />
                          </button>
                          {openMenuId === c.id && (
                            <>
                              <div onClick={() => setOpenMenuId(null)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                              <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 41, background: '#181b22', border: `1px solid ${palette.borderStrong}`, borderRadius: 8, minWidth: 170, boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
                                <MenuItem onClick={() => { setOpenMenuId(null); onEditClient(c); }}>Edit client</MenuItem>
                                <MenuItem onClick={() => { setOpenMenuId(null); onDeliverySummary(c); }}>Policy delivery summary</MenuItem>
                                <MenuItem onClick={() => { setOpenMenuId(null); setSelectedClient(c); }}>View details</MenuItem>
                              </div>
                            </>
                          )}
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 16px', borderTop: `1px solid ${palette.border}`, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: palette.textFaint }}>
              Rows per page
              <select value={rowsPerPage} onChange={e => setRowsPerPage(Number(e.target.value))} style={{ ...selStyle(), padding: '4px 8px' }}>
                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span>&middot; {filteredRows.length} client{filteredRows.length === 1 ? '' : 's'}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={{ ...btnStyle(false), padding: '5px 8px', opacity: page <= 1 ? 0.4 : 1 }}><ChevronLeft size={14} /></button>
              <span style={{ fontSize: 11.5, color: palette.textFaint, fontFamily: fontMono }}>{page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={{ ...btnStyle(false), padding: '5px 8px', opacity: page >= totalPages ? 0.4 : 1 }}><ChevronRight size={14} /></button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom chart cards */}
      {clients.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
          <ChartCard testId="chart-plantype" title="Clients by Draft Frequency" caption="Billing cadence across the clients in view — the dimension tracked for every book. Use the Book control above to slice by product line.">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Donut data={draftFreqData} />
              <Legend data={draftFreqData} />
            </div>
          </ChartCard>
          <ChartCard title="Clients by Carrier" caption="Top carriers by client count.">
            <BarRows data={carrierChartData} />
          </ChartCard>
          <ChartCard testId="chart-status" title="Policy Status Overview">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Donut data={statusChartData} />
              <Legend data={statusChartData} />
            </div>
          </ChartCard>
          <ChartCard title="Upcoming Renewals" caption="Effective date + 1 year, next up first.">
            {upcomingRenewals.length === 0 ? (
              <div style={{ fontSize: 12, color: palette.textFaint, fontStyle: 'italic' }}>No upcoming renewals on file. Issue dates aren't set for these policies.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {upcomingRenewals.map(({ c, renewal }) => (
                  <div key={c.id} onClick={() => setSelectedClient(c)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <Avatar name={c.name} size={22} />
                      <span style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                    </div>
                    <span style={{ fontFamily: fontMono, fontSize: 11, color: palette.textFaint, display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                      <Clock size={11} /> {renewal.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </ChartCard>
        </div>
      )}

      {selectedClient && (
        <ClientDrawer
          client={selectedClient}
          riskByClient={riskByClient}
          agentName={getAgentName(selectedClient.prod_num)}
          onClose={() => setSelectedClient(null)}
          onEdit={(c) => { setSelectedClient(null); onEditClient(c); }}
          onDelivery={(c) => { setSelectedClient(null); onDeliverySummary(c); }}
          showToast={showToast}
          refreshClients={refreshClients}
          estimateNextDraft={estimateNextDraft}
          onGotoTab={onGotoTab}
        />
      )}
    </div>
  );
}

function Th({ children, align = 'left' }) {
  return (
    <th style={{ textAlign: align, padding: '9px 14px', fontFamily: fontMono, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: palette.textFaint, whiteSpace: 'nowrap' }}>
      {children}
    </th>
  );
}
function Td({ children, align = 'left' }) {
  return <td style={{ textAlign: align, padding: '10px 14px', verticalAlign: 'middle', color: palette.text }}>{children}</td>;
}
function MenuItem({ children, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', fontSize: 12.5, background: 'transparent', border: 'none', color: palette.textSecondary, cursor: 'pointer' }}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = palette.text; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = palette.textSecondary; }}
    >
      {children}
    </button>
  );
}
function CheckboxRow({ label, checked, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: palette.textSecondary, padding: '5px 0', cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      {label}
    </label>
  );
}
function FilterPopoverBody({ riskOnly, setRiskOnly, downlineOnly, setDownlineOnly, missingPhoneOnly, setMissingPhoneOnly }) {
  return (
    <div>
      <div style={{ fontFamily: fontMono, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: palette.amber, marginBottom: 8 }}>Filters</div>
      <CheckboxRow label="Payment risk only" checked={riskOnly} onChange={setRiskOnly} />
      <CheckboxRow label="Downline policies only" checked={downlineOnly} onChange={setDownlineOnly} />
      <CheckboxRow label="Missing phone number" checked={missingPhoneOnly} onChange={setMissingPhoneOnly} />
    </div>
  );
}
function ColumnsPopoverBody({ hiddenCols, toggleCol }) {
  return (
    <div>
      <div style={{ fontFamily: fontMono, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: palette.amber, marginBottom: 8 }}>Show / Hide Columns</div>
      <CheckboxRow label="Member ID" checked={!hiddenCols.has('member_id')} onChange={() => toggleCol('member_id')} />
      <CheckboxRow label="Effective Date" checked={!hiddenCols.has('effective_date')} onChange={() => toggleCol('effective_date')} />
      <CheckboxRow label="Agent" checked={!hiddenCols.has('agent')} onChange={() => toggleCol('agent')} />
    </div>
  );
}
